import { Delta } from 'quill/core';
import Prism from './Prism';
import CodeEmbedToken from '../formats/namespaces/code/CodeEmbedToken';

function getTokenText(token: string | Prism.Token): string {
  if (typeof token === 'string') {
    return token;
  }
  const { content } = token;
  if (typeof content === 'string') {
    return content;
  }

  return Array.isArray(content)
    ? content.map(getTokenText).join('')
    : getTokenText(content);
}

function tokenStreamToDelta(
  tokens: (string | Prism.Token)[],
  currentValue: string[] = [],
): Delta {
  let delta = new Delta();
  tokens.forEach((token) => {
    if (typeof token === 'string') {
      delta.retain(token.length);
      return;
    }
    const { type, content, length } = token;
    const newValue = [...currentValue, type];
    if (content) {
      const nestedDelta = tokenStreamToDelta(
        Array.isArray(content) ? content : [content],
        newValue,
      );
      delta = delta.concat(
        new Delta().compose(
          new Delta()
            .retain(length, { [CodeEmbedToken.keyName]: newValue })
            .compose(nestedDelta),
        ),
      );
    } else {
      delta.retain(length, { [CodeEmbedToken.keyName]: newValue });
    }
  });
  return delta;
}

interface GetDeltaForSyntaxHighlighting {
  grammar: Prism.Grammar;
  maxTokenTotal: number;
  maxTokenPerLine: number;
}

const getDeltaForSyntaxHighlighting = (
  code: string,
  options: GetDeltaForSyntaxHighlighting,
) => {
  const tokens = Prism.tokenize(code, options.grammar);

  const truncatedTokens: (string | Prism.Token)[] = [];
  let tokenCountTotal = 0;
  let tokenCountPerLine = 0;
  for (const token of tokens) {
    tokenCountTotal += 1;
    if (tokenCountTotal > options.maxTokenTotal) {
      break;
    }

    const text = getTokenText(token);
    if (text.includes('\n')) {
      truncatedTokens.push(token);
      tokenCountPerLine = 0;
    } else if (tokenCountPerLine < options.maxTokenPerLine) {
      truncatedTokens.push(token);
      tokenCountPerLine += 1;
    } else {
      truncatedTokens.push(text);
    }
  }

  return tokenStreamToDelta(truncatedTokens).chop();
};

export default getDeltaForSyntaxHighlighting;
